import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-xl font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-[var(--outline-border)] bg-white text-[#101828] shadow-[var(--shadow-card)] hover:border-[rgba(147,51,245,0.4)] hover:bg-[var(--lm-hover)] dark:border-white/20 dark:bg-transparent dark:text-white dark:shadow-none dark:hover:border-white/30 dark:hover:bg-white/10 text-sm gap-2",
        /** Compact ghost-outline control used in composer cards and integration actions */
        subtle:
          "border border-[rgba(15,23,42,0.09)] bg-transparent text-[#101828] shadow-none hover:bg-[var(--lm-hover)] dark:border-white/10 dark:text-white dark:hover:bg-white/5",
        secondary: "bg-primary/10 text-primary hover:bg-primary/20",
        /** Dark slate control (e.g. secondary actions) — solid fill, no border */
        surface:
          "border-0 bg-[var(--surface-sunken)] text-[#101828] shadow-none hover:brightness-[0.98] dark:bg-secondary dark:text-slate-200 dark:hover:brightness-110 dark:hover:text-slate-50",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        accent: "bg-[#9333F5] text-primary-foreground hover:bg-[#9333F5]/90 text-sm gap-2 shadow-lg shadow-primary/15",
        icon:
          "border border-[var(--outline-border)] bg-white text-[#101828] shadow-[var(--shadow-card)] hover:border-[rgba(147,51,245,0.4)] hover:bg-[var(--lm-hover)] dark:border-white/20 dark:bg-transparent dark:text-white dark:shadow-none dark:hover:border-white/30 dark:hover:bg-white/10",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-8 w-8 border border-[var(--outline-border)] bg-white text-[#101828] shadow-[var(--shadow-card)] hover:border-[rgba(147,51,245,0.4)] hover:bg-[var(--lm-hover)] dark:border-white/20 dark:bg-transparent dark:text-white dark:shadow-none dark:hover:border-white/30 dark:hover:bg-white/10 [&_svg]:size-4",
        xs: "h-7 gap-1.5 px-2.5 text-xs font-medium [&_svg]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
